import React, { PureComponent } from 'react';
import { Flex } from '../../common/Flex';
import { Range, TimeScale } from '../../../common/graph/Scale';
import TimeUtils from '../../../common/util/TimeUtils';
import { TimePeriod } from '../../../model/CORE';
import Utils from 'app/common/util/Utils';

interface TimeSelectorProps {
    label?: string;
    width: string;
    timePeriod: TimePeriod;
    markIntervalMs: number;
    onChange?: (value: Date) => void;
    highlights?: Array<TimePeriod>;
    value: Date;
    currentTickColor?: string;
}

interface TimeSelectorState {
    scale: TimeScale;
}

export class TimeSelector extends PureComponent<TimeSelectorProps, TimeSelectorState> {
    clicked = false;
    svgRef: React.RefObject<SVGSVGElement> = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            scale: this.props.timePeriod && TimeScale.newTimeScale(new Range(0, 100), this.props.timePeriod),
        };
    }

    componentDidUpdate(prevProps, prevState): void {
        if (prevProps.timePeriod !== this.props.timePeriod) {
            this.setState({ scale: TimeScale.newTimeScale(new Range(0, 100), this.props.timePeriod) });
        }
    }

    renderMarks = () => {
        if (!this.state.scale) return undefined;

        let time: Date = this.state.scale.toDate(this.state.scale.getScale().target.from);
        const marks = [];
        while (TimeUtils.diffInMs(this.state.scale.toDate(this.state.scale.getScale().target.to), time) > 0) {
            const x = this.state.scale.toSource(time);
            marks.push(
                <line
                    key={`mark-${marks.length}`}
                    x1={x + '%'}
                    y1="25%"
                    x2={x + '%'}
                    y2="80%"
                    stroke="#FFFF0030"
                    strokeWidth="0.1em"
                    shapeRendering="geometricPrecision"
                />,
            );
            time = TimeUtils.addMillis(time, this.props.markIntervalMs);
        }
        return marks;
    };
    renderCursor = () => {
        if (!this.state.scale || !this.props.value) return undefined;

        const x = this.state.scale.toSource(this.props.value);
        return [
            <line
                key="cursor-line"
                x1={x + '%'}
                y1="0%"
                x2={x + '%'}
                y2="100%"
                stroke={Utils.isExists(this.props.currentTickColor) ? this.props.currentTickColor : '#FFFF00'}
                strokeWidth="0.1em"
                shapeRendering="geometricPrecision"
            />,
            <rect
                key="cursor-rect"
                x={x.toFixed(5) + '%'}
                y="0%"
                width={'0.3em'}
                height={'0.3em'}
                fill={Utils.isExists(this.props.currentTickColor) ? this.props.currentTickColor : '#FFFF00'}
                style={{ transform: 'translateX(-0.15em)' }}
            />,
        ];
    };
    renderHighlights = () => {
        if (!this.state.scale || !this.props.highlights) return undefined;

        const elements = [];
        let index = 0;
        for (const highlightTimePeriod of this.props.highlights) {
            const coordinateStart = this.state.scale.toSource(highlightTimePeriod.from);
            const coordinateEnd = this.state.scale.toSource(highlightTimePeriod.to);

            elements.push(
                <line
                    key={`ts-highlight-${index++}`}
                    y1={'90%'}
                    x1={coordinateStart + '%'}
                    y2={'90%'}
                    x2={coordinateEnd + '%'}
                    stroke="red"
                    strokeWidth="0.4em"
                    shapeRendering="geometricPrecision"
                />,
            );
        }
        return elements;
    };
    handleMouseDown = e => {
        this.clicked = true;
        this.handleMouseMove(e);
    };
    handleMouseUp = e => {
        this.clicked = false;
    };
    handleMouseMove = e => {
        if (!this.clicked || !this.state.scale) return undefined;

        const rect = this.svgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        if (x > 0) {
            const newTime = TimeUtils.withoutSeconds(this.state.scale.toTarget((x * 100) / rect.width));
            if (this.props.onChange) this.props.onChange(newTime);
        }
    };
    handleMouseOut = e => {
        this.clicked = false;
    };
    renderRefTime = () => {
        if (!this.state.scale || !this.props.value) return undefined;

        return (
            <Flex size="20%" style={{ position: 'relative', alignItems: 'flex-end' }}>
                <span
                    style={{
                        position: 'absolute',
                        left: `${this.state.scale?.toSource(this.props.value) || 0}%`,
                        transform: 'translateX(-50%)',
                        fontWeight: 'bold',
                        fontSize: '0.85em',
                        color: 'white',
                    }}>
                    {TimeUtils.formatHHMM(this.props.value)}
                </span>
            </Flex>
        );
    };
    render() {
        return (
            <>
                {this.props.label && (
                    <Flex direction="vertical" style={{ flex: '0 0 auto', margin: '0 1em' }}>
                        <Flex size="20%" />
                        <Flex>
                            <span style={{ fontWeight: 'bold', alignSelf: 'center' }}>{this.props.label}</span>
                        </Flex>
                    </Flex>
                )}
                <Flex direction="vertical" size={this.props.width} style={{ justifyContent: 'center' }}>
                    {this.renderRefTime()}
                    <Flex style={{ position: 'relative' }}>
                        <span
                            style={{ position: 'absolute', width: '100%', height: '100%' }}
                            onMouseDown={this.handleMouseDown}
                            onMouseMove={this.handleMouseMove}
                            onMouseUp={this.handleMouseUp}
                            onMouseOut={this.handleMouseOut}
                        />
                        <svg
                            ref={this.svgRef}
                            style={{
                                alignSelf: 'center',
                                height: '2.5em',
                                width: this.props.width,
                                shapeRendering: 'crispEdges',
                            }}>
                            {this.renderHighlights()}
                            {this.renderMarks()}
                            {this.renderCursor()}
                        </svg>
                    </Flex>
                </Flex>
            </>
        );
    }
}
